import { makeAutoObservable } from 'mobx'
import type { IIconProps } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type ISmartCallFlowIVRStep } from 'entities/SmartCall/api/types'
import { FlowActionStore } from 'widgets/Flow'
import {
  generateSmartCallActionId,
  type ISmartCallActionStore,
  type ISmartCallFlowActionStoreProps,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/Action'
import { type ISmartCallFlowActionType } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'
import { GreetingAudioBlockStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/blocks/GreetingAudioBlock'
import { type SmartCallFlowBuilderStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder'

export type IIVRStoreProps = {
  id?: ISmartCallFlowActionStoreProps['id']
  parentId: ISmartCallFlowActionStoreProps['parentId']
  args?: ISmartCallFlowIVRStep['args']
}

export class IVRStore {
  static readonly type = 'ivrPhoneTree' satisfies ISmartCallFlowActionType
  static title = 'IVR phone tree'
  static iconProps: IIconProps = {
    icon: 'dataflow',
    color: 'var(--action-contained-primary-normal)',
  }
  public readonly actionStore: ISmartCallActionStore
  brunchLabel = 'No selection'
  readonly greetingMessageStore: GreetingAudioBlockStore

  constructor(
    private _entities: IEntitiesStore,
    { id, args, ...props }: IIVRStoreProps,
    private parent: SmartCallFlowBuilderStore
  ) {
    this.actionStore = new FlowActionStore({
      ...props,
      id: id || generateSmartCallActionId(`${this._stepIdType.type}_${this._stepIdType.id}`),
      type: IVRStore.type,
      name: IVRStore.title,
      allowedNextActions: ['callingFlow', 'playSound', 'sendVoicemail'],
      allowedBeforeActions: [],
      edgeType: 'customEdgeWithLabel',
    })
    this.greetingMessageStore = new GreetingAudioBlockStore(
      this._entities,
      this.parent.parent,
      'Your message is the key – please select an audio',
      args?.greeting_audio_url
        ? {
            audio_url: args?.greeting_audio_url,
          }
        : undefined
    )

    makeAutoObservable(this)
  }

  private _stepIdType: Pick<ISmartCallFlowIVRStep, 'id' | 'type'> = {
    id: IVRStore.type,
    type: 'action',
  }

  get step(): ISmartCallFlowIVRStep {
    return {
      ...this._stepIdType,
      args: {
        greeting_audio_url: this.greetingMessageStore.args.audio_url,
      },
      next: this.actionStore.nextId,
    }
  }

  setApiError = (key: string, message: string) => {}

  validate = () => {
    let valid = true
    if (!this.greetingMessageStore.validate()) {
      valid = false
    }
    return valid
  }

  get isConfigurationError() {
    return !!this.greetingMessageStore.error
  }

  brunches: Array<{ key: number | null; title: string; nextId: string }> = []
  getBranchNameByTargetId = (id: string) => {
    const index = this.actionStore.nextIds.findIndex((nextId) => nextId === id)
    const data = this.brunches[index]
    if (data?.key) {
      return `Key ${data.key} → ${data.title}`
    }
    return 'No selection'
  }
}
